import { useCallback, useEffect, useState } from "react"
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import type { RootState } from "@/store"
import { useGetFormasPagamentoLojaQuery, useGetLojaAtualQuery, type FormaPagamentoLoja } from "@/api"
import { atualizarKiosk } from "@/store/self-service-kiosk-slice"
import { trocarView, resetarView } from "../navegacao-kiosk"
import { capitalize, formatarBRL } from "@/lib/formato"
import ScreenHeader from "../components/screen-header"
import LayoutPassos, { useTelaDeitada } from "../components/layout-passos"
import AvisoInatividade from "@/components/totem/aviso-inatividade"
import StatusFeedback from "../components/status-feedback"
import { calcularConta } from "../items-screen/calculo-conta"
import { perguntaCpfNaNota } from "@/lib/impressao-totem"
import PaymentProcessing, { MS_EXIBICAO_APROVADO } from "./payment-processing"
import ValorInput from "./valor-input"
import PosPagamento from "./pos-pagamento"
import ListaFormas from "./lista-formas"
import EscolhaMetodo from "./escolha-metodo"
import PagueNoCaixa from "./pague-no-caixa"
import StepperPagamento from "./stepper-pagamento"
import { formasDoCheckout, metodosDoCheckout, pagaNoCaixa, passoAtivo, passosDoPagamento, ROTULO_PASSO_PAGAMENTO, type EtapaPosPagamento } from "./passos-pagamento"
import { usePagamentoTef } from "./use-pagamento-tef"
import { useReconferirConta } from "./use-reconferir-conta"

const INATIVIDADE_MS = 120_000

export default function PaymentScreen(): JSX.Element {
    const dispatch = useDispatch()
    const order = useSelector((state: RootState) => state.selfServiceKiosk.order)
    const modeloIdentificador = useSelector((state: RootState) => state.selfServiceKiosk.modeloIdentificador)
    const identificadorBuscado = useSelector((state: RootState) => state.selfServiceKiosk.identificadorBuscado)
    const emiteNfce = useSelector((state: RootState) => state.selfServiceKiosk.emiteNfce)
    const modoPagamento = useSelector((state: RootState) => state.selfServiceKiosk.modoPagamento)
    const contaInteira = modoPagamento === 'CONTA_INTEIRA'
    const { data: loja } = useGetLojaAtualQuery()
    const impressaoCheckout = loja?.configuracao.totemCheckoutImpressao ?? 'NFCE'
    const { data: formasDaLoja, isError: erroFormas, refetch: recarregarFormas } = useGetFormasPagamentoLojaQuery(undefined, { skip: !loja })
    const formas = formasDoCheckout(formasDaLoja)
    const deitado = useTelaDeitada()

    const rotuloPrincipal = capitalize(modeloIdentificador)
    const rotuloOposto = modeloIdentificador === 'COMANDA' ? 'Mesa' : 'Comanda'
    const { total, valorRecebido, pendente } = calcularConta(order)
    const totalCentavos = Math.round(pendente * 100)
    const reconferencia = useReconferirConta()
    const [valorEscolhidoCentavos, setValorEscolhidoCentavos] = useState<number | null>(null)
    const [centavosNaoRegistrados, setCentavosNaoRegistrados] = useState(0)
    const [formaEscolhida, setFormaEscolhida] = useState<FormaPagamentoLoja | null>(null)
    const [etapaPos, setEtapaPos] = useState<EtapaPosPagamento | null>(null)
    const saldoCentavos = Math.max(totalCentavos - centavosNaoRegistrados, 0)
    // Valor digitado que deixaria 1 centavo em aberto leva o centavo junto: ninguém volta para pagar R$ 0,01.
    const centavoIncluido = !contaInteira && valorEscolhidoCentavos != null && saldoCentavos - valorEscolhidoCentavos === 1
    const valorAtualCentavos = contaInteira || centavoIncluido ? saldoCentavos : (valorEscolhidoCentavos ?? 0)
    const ehQuitacao = valorAtualCentavos >= saldoCentavos
    const valorDefinido = contaInteira || valorEscolhidoCentavos != null

    const totalFormatado = formatarBRL(pendente)

    const { metodo: metodoSelecionado, cobranca, fase, mensagemErro, avisoRegistro, contaNaoFechou, contaFechada, registrando, saldoRestanteCentavos, ultimoResultado, iniciar, resetar } = usePagamentoTef({
        valorCentavos: valorAtualCentavos,
        idPedidoIntegracao: order?.idPedidoIntegracao ?? null,
        fecharPedido: ehQuitacao,
    })

    // Com a cobrança em curso vale o que foi cobrado: registrado o pagamento o saldo cai, e a conta viva diria que metade quitou.
    const valorCobradoCentavos = cobranca?.valorCentavos ?? valorAtualCentavos
    // O Sischef pode fechar com uma parte: o resto entrou pelo caixa enquanto o cliente pagava aqui.
    const quitaAConta = contaFechada || (cobranca?.fecharPedido ?? ehQuitacao)
    // O que falta para quem está no totem: o saldo do Sischef menos o que foi cobrado e não entrou na comanda; sem ele (registro em curso ou falho), o saldo menos esta cobrança.
    const saldoDepoisCentavos = saldoRestanteCentavos != null
        ? Math.max(saldoRestanteCentavos - centavosNaoRegistrados, 0)
        : Math.max(saldoCentavos - valorCobradoCentavos, 0)

    const valorAcimaDoSaldo = !metodoSelecionado && !contaInteira && valorEscolhidoCentavos != null && valorEscolhidoCentavos > saldoCentavos

    useEffect(() => {
        if (saldoRestanteCentavos == null || !order) return

        const recebido = Math.max(Math.round((order.total ?? 0) * 100) - saldoRestanteCentavos, 0) / 100
        if (Math.abs(recebido - (order.valorRecebido ?? 0)) < 0.005) return

        dispatch(atualizarKiosk({ order: { ...order, valorRecebido: recebido } }))
    }, [saldoRestanteCentavos, order, dispatch])

    const valorAtualFormatado = formatarBRL(valorCobradoCentavos / 100)

    const contaPaga = !!metodoSelecionado && fase === 'aprovado' && quitaAConta && !avisoRegistro && !registrando
    const [feedbackAprovadoExibido, setFeedbackAprovadoExibido] = useState(false)

    useEffect(() => {
        if (!contaPaga || !metodoSelecionado) {
            setFeedbackAprovadoExibido(false)
            return
        }

        const timer = setTimeout(() => setFeedbackAprovadoExibido(true), MS_EXIBICAO_APROVADO[metodoSelecionado.trilha])
        return () => clearTimeout(timer)
    }, [contaPaga, metodoSelecionado])

    const pagamentoConcluido = contaPaga && feedbackAprovadoExibido

    const emiteNota = impressaoCheckout === 'NFCE' && emiteNfce
    // Dividindo, CPF e nota só aparecem quando o valor escolhido (ou o Sischef) fecha a conta.
    const perguntaCpf = perguntaCpfNaNota(loja?.configuracao)
    const noCaixa = !!formaEscolhida && pagaNoCaixa(formaEscolhida)
    const passos = passosDoPagamento({ dividindo: !contaInteira, emiteNota, fechaAConta: valorDefinido && quitaAConta, perguntaCpf, pagaNoCaixa: noCaixa })
    const passoAtual = passoAtivo(passos, {
        valorDefinido,
        pagando: !!formaEscolhida || !!metodoSelecionado,
        etapaPos: pagamentoConcluido ? etapaPos : null,
    })

    const voltarParaFormas = useCallback((): void => {
        setFormaEscolhida(null)
        resetar()
    }, [resetar])

    const pagarOutraParte = useCallback((): void => {
        if (avisoRegistro && !contaNaoFechou) setCentavosNaoRegistrados((c) => c + valorCobradoCentavos)
        setValorEscolhidoCentavos(null)
        setFormaEscolhida(null)
        resetar()
    }, [avisoRegistro, contaNaoFechou, valorCobradoCentavos, resetar])

    const { reconferir } = reconferencia
    const escolherValor = useCallback((centavos: number): void => {
        setValorEscolhidoCentavos(centavos)
        reconferir()
    }, [reconferir])

    const escolherForma = (forma: FormaPagamentoLoja): void => {
        setFormaEscolhida(forma)
        if (pagaNoCaixa(forma)) return

        const [unico, ...outros] = metodosDoCheckout(forma)
        if (unico && outros.length === 0) iniciar(unico, forma.id)
    }

    const concluirPagamento = useCallback((): void => {
        resetarView(dispatch)
    }, [dispatch])

    const onClickBack = (): void => {
        if (formaEscolhida) {
            setFormaEscolhida(null)
            return
        }

        if (!contaInteira && valorEscolhidoCentavos != null) {
            setValorEscolhidoCentavos(null)
            return
        }

        trocarView(dispatch, { currentView: 'VIEW_ITEMS' })
    }

    const tefEmAndamento = !!metodoSelecionado && (fase === 'processando' || registrando)

    const identificadorSecundario = order?.identificadorSecundario
        ? `${rotuloOposto} ${order.identificadorSecundario}`
        : 'Sem Mesa'

    const subtituloSaldo = valorRecebido > 0 && pendente > 0 ? ` · Falta ${formatarBRL(pendente)}` : ''

    // Com a forma de mais de um método, o comprovante diz qual foi: "Cartão · Débito".
    const formaRotulo = formaEscolhida && metodosDoCheckout(formaEscolhida).length > 1
        ? `${formaEscolhida.descricao} · ${metodoSelecionado?.rotulo ?? ''}`
        : formaEscolhida?.descricao ?? metodoSelecionado?.rotulo ?? ''

    const seletorDeFormas = erroFormas ? (
        <StatusFeedback
            status="alerta"
            titulo="Não foi possível carregar as formas de pagamento"
            detalhe="Toque para tentar de novo — se continuar, chame um atendente."
            textoBotao="Tentar novamente"
            onDismiss={() => void recarregarFormas()}
            autoRetornoMs={null}
            embutido
        />
    ) : formas == null ? (
        <CircularProgress />
    ) : formas.length === 0 ? (
        <Alert severity="warning" sx={{ maxWidth: 650, width: '80%', fontSize: '1.1rem' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Pagamento no totem indisponível
            </Typography>
            <Typography variant="body1">
                Nenhuma forma de pagamento foi liberada para este totem. Chame um atendente para fechar a conta no caixa.
            </Typography>
        </Alert>
    ) : (
        <ListaFormas formas={formas} onEscolher={escolherForma} colunas={deitado ? 2 : 1} />
    )

    const conteudo = pagamentoConcluido ? (
        <PosPagamento
            idPedidoIntegracao={order?.idPedidoIntegracao ?? null}
            valorFormatado={valorAtualFormatado}
            formaRotulo={formaRotulo}
            nsu={ultimoResultado?.nsu ?? null}
            emiteNfce={emiteNfce}
            impressao={impressaoCheckout}
            imprimirNotaSempre={loja?.configuracao.totemCheckoutNota === 'SEMPRE'}
            perguntarCpf={perguntaCpf}
            avisoRegistroPendente={
                centavosNaoRegistrados > 0
                    ? `${formatarBRL(centavosNaoRegistrados / 100)} não entrou na comanda. Chame um atendente.`
                    : null
            }
            onConcluir={concluirPagamento}
            aoMudarEtapa={setEtapaPos}
        />
    ) : metodoSelecionado ? (
        <PaymentProcessing
            metodo={metodoSelecionado}
            fase={fase}
            valorFormatado={valorAtualFormatado}
            mensagemErro={mensagemErro}
            avisoRegistro={avisoRegistro}
            contaNaoFechou={contaNaoFechou}
            registrando={registrando}
            ehQuitacao={quitaAConta}
            saldoFormatado={formatarBRL(saldoDepoisCentavos / 100)}
            onConcluir={concluirPagamento}
            onProximaPessoa={pagarOutraParte}
            onEncerrar={concluirPagamento}
            onVoltarSelecao={voltarParaFormas}
        />
    ) : formaEscolhida && noCaixa ? (
        <PagueNoCaixa
            formaDescricao={formaEscolhida.descricao}
            valorFormatado={valorAtualFormatado}
            totalFormatado={quitaAConta ? null : totalFormatado}
            identificador={`${rotuloPrincipal} ${identificadorBuscado}`}
            onConcluir={concluirPagamento}
        />
    ) : formaEscolhida ? (
        <EscolhaMetodo
            forma={formaEscolhida}
            valorFormatado={valorAtualFormatado}
            onEscolher={(metodo) => iniciar(metodo, formaEscolhida.id)}
        />
    ) : (
        <Stack spacing={6} sx={{ flex: '1 0 auto', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            {valorDefinido && (
                <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', }}>
                        {quitaAConta ? 'Total a pagar' : 'Você vai pagar'}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Typography variant="h1" sx={{ color: 'success.main', fontWeight: '900', fontSize: '64px' }}>
                            {valorAtualFormatado}
                        </Typography>

                        {!quitaAConta && (
                            <Typography variant="h4" sx={{ pb: 1, pl: 1, color: 'secondary.main', fontWeight: 'bold' }}>
                                de {totalFormatado}
                            </Typography>
                        )}
                    </Box>

                    {centavoIncluido && (
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                            Inclui {formatarBRL(0.01)} para a conta não ficar com um centavo em aberto.
                        </Typography>
                    )}

                    {valorRecebido > 0 && (
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                            Já pago nesta conta: {formatarBRL(valorRecebido)} de {formatarBRL(total)}
                        </Typography>
                    )}
                </Stack>
            )}

            {centavosNaoRegistrados > 0 && (
                <Alert severity="warning" variant="filled" sx={{ maxWidth: 650, width: '80%', fontSize: '1.1rem' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Chame um atendente
                    </Typography>

                    <Typography variant="body1">
                        {formatarBRL(centavosNaoRegistrados / 100)} já foi cobrado no cartão, mas não entrou nesta{' '}
                        {rotuloPrincipal.toLowerCase()}.
                    </Typography>
                </Alert>
            )}

            {reconferencia.verificando ? (
                <Stack spacing={2} sx={{ alignItems: 'center' }}>
                    <CircularProgress />
                    <Typography variant="h6" color="text.secondary">
                        Conferindo a conta...
                    </Typography>
                </Stack>
            ) : reconferencia.erro ? (
                <StatusFeedback
                    status="alerta"
                    titulo="Não foi possível conferir a conta"
                    detalhe="Sem confirmar o valor não podemos cobrar. Toque para tentar de novo — se continuar, chame um atendente."
                    textoBotao="Tentar novamente"
                    onDismiss={reconferencia.tentarNovamente}
                    autoRetornoMs={null}
                    embutido
                />
            ) : reconferencia.semVinculo ? (
                <Alert severity="warning" sx={{ maxWidth: 650, width: '60%', fontSize: '1.1rem' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Não foi possível confirmar esta conta
                    </Typography>
                    <Typography variant="body1">
                        Chame um atendente para fechar no caixa.
                    </Typography>
                </Alert>
            ) : reconferencia.pedidoIndisponivel ? (
                <Alert severity="warning" sx={{ maxWidth: 650, width: '60%', fontSize: '1.1rem' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Esta conta não está mais em aberto
                    </Typography>
                    <Typography variant="body1">
                        Ela pode ter sido fechada no caixa. Chame um atendente antes de pagar.
                    </Typography>
                </Alert>
            ) : reconferencia.totalAnterior !== null ? (
                <Stack spacing={3} sx={{ alignItems: 'center', maxWidth: 650, width: '100%' }}>
                    <Alert severity="info" sx={{ width: '60%', fontSize: '1.1rem' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            A conta foi atualizada pelo caixa.
                        </Typography>
                        <Typography variant="body1">
                            O novo total é <strong>{totalFormatado}</strong> (antes:{' '}
                            {formatarBRL(reconferencia.totalAnterior)}).
                        </Typography>
                    </Alert>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={reconferencia.confirmarCiencia}
                        sx={{ minHeight: 64, borderRadius: 3, textTransform: 'none', px: 5, width: '60%' }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            Entendi, continuar
                        </Typography>
                    </Button>
                </Stack>
            ) : valorAcimaDoSaldo ? (
                <Stack spacing={3} sx={{ alignItems: 'center', maxWidth: 650, width: '100%' }}>
                    <Alert severity="info" sx={{ width: '60%', fontSize: '1.1rem' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            A conta mudou enquanto você escolhia.
                        </Typography>
                        <Typography variant="body1">
                            Agora faltam <strong>{formatarBRL(saldoCentavos / 100)}</strong> — escolha o valor de
                            novo.
                        </Typography>
                    </Alert>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => setValorEscolhidoCentavos(null)}
                        sx={{ minHeight: 64, borderRadius: 3, textTransform: 'none', px: 5, width: '60%' }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            Escolher outro valor
                        </Typography>
                    </Button>
                </Stack>
            ) : !valorDefinido ? (
                <ValorInput saldoCentavos={saldoCentavos} aoConfirmar={escolherValor} />
            ) : (
                seletorDeFormas
            )}
        </Stack>
    )

    return (
        <Stack sx={{ width: '100%', height: '100vh' }}>
            <ScreenHeader
                title="Pagamento"
                subTitle={`${rotuloPrincipal} ${identificadorBuscado} · ${identificadorSecundario}${subtituloSaldo}`}
                onClickBack={onClickBack}
                voltarDesabilitado={!!metodoSelecionado}
            />

            <LayoutPassos
                deitado={deitado}
                stepper={<StepperPagamento rotulos={passos.map((p) => ROTULO_PASSO_PAGAMENTO[p])} ativo={passoAtual} deitado={deitado} />}
            >
                {conteudo}
            </LayoutPassos>

            <AvisoInatividade
                ativo={!tefEmAndamento && !pagamentoConcluido}
                msInatividade={INATIVIDADE_MS}
                aoExpirar={concluirPagamento}
            />
        </Stack>
    )
}
